import React, { memo, ReactNode, useMemo } from "react";
import "moment/locale/fr";
import { GenericCard } from "components/display/Card/Card";
import GenericBadge from "components/ui/Badge";
import { GenericColors } from "enums/genericColors";
import { CardState, ContractStatus } from "../../types/cardState";
import { NsKey } from "enums/NsKey";
import { useTranslation } from "react-i18next";
import { getContractStatusLabel } from "../../utils/getContractCardInfos";

type LightContractCardProps = {
  icon: ReactNode
  title?: string
  subtitle?: string
  className?: string
  status: CardState
  /** Click handler global de la card. Si omis, la card n'est pas cliquable (utile en read-only). */
  onClick?: () => void
  disabled?: boolean
  /** Si fourni, remplace le badge d'état auto à droite du header (utile pour mode read-only). */
  titleAsideOverride?: ReactNode
};

export const LightContractCard: React.FC<LightContractCardProps> = memo(({
  icon,
  className,
  title,
  subtitle,
  status,
  onClick,
  disabled,
  titleAsideOverride
}) => {
  const { t } = useTranslation(NsKey.Contractualization)

  const badgeProps = useMemo(() => {
    switch (status) {
      case CardState.CRITICAL:
        return {
          color: GenericColors.Red,
          iconKey: "exclamationPoint",
        }
      case CardState.VALID: {
        if (disabled)
          return {
            color: GenericColors.Green,
            text: getContractStatusLabel(ContractStatus.Completed),
          }
        return {
          color: GenericColors.Green,
          text: getContractStatusLabel(ContractStatus.Completed),
        }
      }
      default:
        return {
          color: GenericColors.Red,
          text: getContractStatusLabel(ContractStatus.MissingParts),
        }
    }
  }, [status, disabled])

  return (
    <GenericCard
      border={!disabled}
      className={className}
    >
      <GenericCard.Header
        disabled={disabled}
        icon={icon}
        onClick={onClick}
        subtitle={subtitle && t(subtitle)}
        titleAside={titleAsideOverride ?? <GenericBadge iconKey={badgeProps.iconKey} color={badgeProps.color} label={badgeProps.text} badgeType={badgeProps.iconKey ? 'round' : 'status'} />}
        className="relative"
      >
        {title && t(title)}
      </GenericCard.Header>
    </GenericCard>
  );
});

LightContractCard.displayName = 'LightContractCard';
